import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import SocialLinks from './SocialLinks';

const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const navOffset = 150;
      const scrollPosition = window.scrollY;
      setIsSticky(scrollPosition > navOffset);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: 'Our Services', path: '/#our-services' },
    { name: 'How It\'s Work', path: '/#how-its-work' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact Us', path: '/#contact-us' },
  ];

  return (
    <header className="absolute pt-4 top-0 left-0 right-0 z-50">
      <div className="container mx-auto px-4 py-6">
        {/* Top Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 md:mb-4">
          {/* Social Links with circular background */}
          <div className="flex items-center space-x-4 bg-white/10 rounded-full px-6 py-2 mb-6 md:mb-0">
            <SocialLinks />
          </div>

          <a href="/" className="transition-opacity hover:opacity-80">
            <Logo />
          </a>
        </div>
        
        {/* Border Line Top */}
        <div className="w-full h-px bg-line"></div>
        
        {/* Desktop Navigation - Sticky */}
        <div className={`hidden md:block w-full ${isSticky ? 'fixed top-0 left-0 right-0 bg-black/90 backdrop-blur-sm shadow-lg z-50 transition-all duration-300' : ''}`}>
          <nav className={`hidden md:flex justify-between items-center py-4 w-full ${isSticky ? 'max-w-7xl mx-auto px-16' : 'px-16'}`}>
            {menuItems.map((item, index) => (
              <React.Fragment key={item.name}>
                <a 
                  href={item.path}
                  className="relative text-white text-sm whitespace-nowrap group px-2 py-1"
                >
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                    {item.name}
                  </span>
                  
                  {/* Effet de hover modifié pour éviter la bordure blanche */}
                  <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left"></span>
                </a>
                {index < menuItems.length - 1 && (
                  <span className="text-line">|</span>
                )}
              </React.Fragment>
            ))}

              <React.Fragment key="career">
                <span 
                  onClick={() => navigate('/career')}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/career')}
                  role="button"
                  tabIndex={0}
                  className="relative text-white text-sm whitespace-nowrap group px-2 py-1 cursor-pointer"
                >
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                    Career
                  </span>
                  
                  {/* Effet de hover modifié pour éviter la bordure blanche */}
                  <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left" />
                </span>
                <span className="text-line">|</span>
              </React.Fragment>

              <React.Fragment key="business">
                <span 
                  onClick={() => navigate('/business')}
                  onKeyDown={(e) => e.key === 'Enter' && navigate('/business')}
                  role="button"
                  tabIndex={0}
                  className="relative text-white text-sm whitespace-nowrap group px-2 py-1 cursor-pointer"
                >
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                    Business
                  </span>
                  
                  {/* Effet de hover modifié pour éviter la bordure blanche */}
                  <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left" />
                </span>
              </React.Fragment>
          </nav>
        </div>
           
        {/* Mobile Menu Button */}
        <button 
          type="button"
          className="md:hidden p-2 absolute right-4 top-8 z-50"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <div className="space-y-2">
            <span className={`block w-8 h-0.5 bg-white transform transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2.5' : ''}`} />
            <span className={`block w-8 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-8 h-0.5 bg-white transform transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2.5' : ''}`} />
          </div>
        </button>
        
        {/* Mobile Menu */}
        <div 
          className={`fixed inset-0 bg-black transition-transform duration-300 ease-in-out transform md:hidden ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <nav className="flex flex-col items-center justify-center h-full space-y-8">
            {menuItems.map((item) => (
              <a
                key={item.name}
                href={item.path}
                className="text-white text-xl font-medium hover:text-gray-300 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </a>
            ))}
              <span
                role="button"
                tabIndex={0}
                className="text-white text-xl font-medium hover:text-gray-300 transition-colors"
                onClick={() => [setIsMenuOpen(false), navigate('/career')]}
                onKeyDown={(e) => e.key === 'Enter' && [setIsMenuOpen(false), navigate('/career')]}
              >
                Career
              </span>
              <span
                role="button"
                tabIndex={0}
                className="text-white text-xl font-medium hover:text-gray-300 transition-colors"
                onClick={() => [setIsMenuOpen(false), navigate('/business')]}
                onKeyDown={(e) => e.key === 'Enter' && [setIsMenuOpen(false), navigate('/business')]}
              >
                Business
              </span>
            
            {/* Mobile Social Links */}
            <div className="mt-8">
              <SocialLinks />
            </div>
          </nav>
        </div>
        
        {/* Border Line Bottom */}
        <div className="w-full h-px bg-line"></div>
      </div>
    </header>
  );
};

export default Header;